/**
 * AdminMemosPage (iter140)
 *
 * Admin-only firehose into every memo across the app:
 *   • Tab 1 — Team Memos: every custom team's roster + post history.
 *   • Tab 2 — Contact Memos: every external-contact memo, grouped by contact.
 *
 * Lives at `/admin/memos`. Reachable from the Globe icon in the
 * `/memos` page header (visible to admins only).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card, CardContent } from '../components/ui/card';
import { Search, Users as UsersIcon, ChevronRight } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { usePageHeader } from '../context/PageHeaderContext';

const initials = (name) => ((name || '').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?');

const AdminMemosPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();

  usePageHeader({ title: 'All Memos · Admin', back: () => navigate('/memos') }, []);

  const [tab, setTab] = useState('teams');
  const [teams, setTeams] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === 'admin';

  const loadTeams = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get('/memos/admin/all-team-memos'); setTeams(data || []); }
    catch (e) { setTeams([]); }
  }, [api, isAdmin]);

  const loadContacts = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get('/memos/admin/all-contact-memos'); setContacts(data || []); }
    catch (e) { setContacts([]); }
  }, [api, isAdmin]);

  useEffect(() => { loadTeams(); loadContacts(); }, [loadTeams, loadContacts]);

  const q = search.trim().toLowerCase();
  // Hooks must NEVER appear after an early return — keep useMemo above
  // the admin-gate render.
  const visibleTeams = useMemo(() => {
    if (!teams) return null;
    if (!q) return teams;
    return teams.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.creator_name || '').toLowerCase().includes(q) ||
      (t.members || []).some((m) => (m.name || '').toLowerCase().includes(q)) ||
      (t.memos || []).some((m) => (m.body || '').toLowerCase().includes(q) || (m.owner_name || '').toLowerCase().includes(q))
    );
  }, [teams, q]);

  const visibleContacts = useMemo(() => {
    if (!contacts) return null;
    if (!q) return contacts;
    return contacts.filter((g) => {
      const c = g.contact || {};
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (g.memos || []).some((m) => (m.body || '').toLowerCase().includes(q) || (m.owner_name || '').toLowerCase().includes(q))
      );
    });
  }, [contacts, q]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Admin only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" data-testid="admin-memos-page">
      <div className="max-w-4xl mx-auto p-4 space-y-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="teams" data-testid="admin-memos-tab-teams">Team Memos</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-memos-tab-contacts">Contact Memos</TabsTrigger>
          </TabsList>
          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teams, contacts, members, memos…" className="pl-9 bg-white/70 dark:bg-[#141414]/70" data-testid="admin-memos-search" />
          </div>

          <TabsContent value="teams" className="mt-4 space-y-2">
            {visibleTeams === null ? <PageLoader label="Loading teams..." fullScreen={false} gradient={false} />
              : visibleTeams.length === 0 ? <p className="text-center text-sm text-muted-foreground py-12">No teams match.</p>
              : visibleTeams.map((t) => (
                <Card key={t.id} className="rounded-2xl hover:bg-muted/30 transition-colors cursor-pointer" data-testid={`admin-team-${t.id}`} onClick={() => navigate(`/admin/memos/team/${t.id}`)}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <UsersIcon className="w-4 h-4 text-[#586D52]" />
                      <span className="font-semibold text-sm">{t.name}</span>
                      <span className="text-[11px] text-muted-foreground">· {(t.members || []).length} members · {(t.memos || []).length} memos</span>
                      <span className="ml-auto"><ChevronRight className="w-4 h-4 text-muted-foreground" /></span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 ml-6">Created by {t.creator_name || '(unknown)'} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</p>
                  </CardContent>
                </Card>
              ))
            }
          </TabsContent>

          <TabsContent value="contacts" className="mt-4 space-y-2">
            {visibleContacts === null ? <PageLoader label="Loading contacts..." fullScreen={false} gradient={false} />
              : visibleContacts.length === 0 ? <p className="text-center text-sm text-muted-foreground py-12">No contacts match.</p>
              : visibleContacts.map((g) => {
                const c = g.contact || {};
                return (
                  <Card key={c.id} className="rounded-2xl hover:bg-muted/30 transition-colors cursor-pointer" data-testid={`admin-contact-${c.id}`} onClick={() => navigate(`/admin/memos/contact/${c.id}`)}>
                    <CardContent className="p-3 flex items-center gap-2">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        {c.profile_picture ? <AvatarImage src={c.profile_picture} alt="" /> : null}
                        <AvatarFallback className="bg-[#586D52]/20 text-[#586D52] text-xs">{initials(c.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{c.name || '(unnamed)'}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{[c.title, c.company].filter(Boolean).join(' · ')} · {g.memo_count || 0} memo{g.memo_count === 1 ? '' : 's'}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </CardContent>
                  </Card>
                );
              })
            }
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminMemosPage;
